import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { HitsterEvent, EventRound, EventTeam } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || '';

const CameraQR: React.FC = () => {
  const [events, setEvents] = useState<HitsterEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [selectedRoundId, setSelectedRoundId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/events`)
      .then(r => r.json())
      .then((data: HitsterEvent[]) => {
        setEvents(data);
        if (data.length > 0) {
          const last = data[data.length - 1];
          setSelectedEventId(last.id);
          if (last.rounds && last.rounds.length > 0) {
            setSelectedRoundId(last.rounds[last.rounds.length - 1].id);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const selectedEvent = events.find(e => e.id === selectedEventId) || null;
  const selectedRound = selectedEvent?.rounds?.find(r => r.id === selectedRoundId) || null;

  const handleEventChange = (eventId: string) => {
    setSelectedEventId(eventId);
    const ev = events.find(e => e.id === eventId);
    if (ev && ev.rounds && ev.rounds.length > 0) {
      setSelectedRoundId(ev.rounds[ev.rounds.length - 1].id);
    } else {
      setSelectedRoundId('');
    }
  };

  const getCameraUrl = (team: EventTeam) => {
    const base = window.location.origin;
    return `${base}/camera?eventId=${selectedEventId}&roundId=${selectedRoundId}&teamId=${team.id}`;
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Laden...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header} className="no-print">
        <h1 style={styles.title}>📲 QR-codes per team</h1>
        <div style={styles.controls}>
          <div style={styles.selectGroup}>
            <label style={styles.label}>Evenement</label>
            <select
              style={styles.select}
              value={selectedEventId}
              onChange={e => handleEventChange(e.target.value)}
            >
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.selectGroup}>
            <label style={styles.label}>Ronde</label>
            <select
              style={styles.select}
              value={selectedRoundId}
              onChange={e => setSelectedRoundId(e.target.value)}
            >
              {selectedEvent?.rounds?.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <button style={styles.printBtn} onClick={handlePrint}>
            🖨️ Afdrukken
          </button>
        </div>
      </div>

      {selectedEvent && selectedRound && (selectedEvent.teams?.length ?? 0) > 0 ? (
        <div style={styles.grid}>
          {selectedEvent.teams!.map(team => (
            <div key={team.id} style={styles.card}>
              <div style={styles.teamName}>{team.name}</div>
              <div style={styles.qrWrapper}>
                <QRCodeSVG
                  value={getCameraUrl(team)}
                  size={180}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#1a1a2e"
                  includeMargin={true}
                />
              </div>
              <div style={styles.eventInfo}>
                {selectedEvent.name} · {selectedRound.name}
              </div>
              <div style={styles.lockLabel}>🔒 team vergrendeld</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.empty}>
          {selectedEvent ? 'Geen teams gevonden voor dit evenement.' : 'Selecteer een evenement.'}
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0; }
          div[data-grid] { gap: 12px !important; padding: 12px !important; }
        }
      `}</style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: '#0f0f1a',
    color: '#e8e8f0',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: '0',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#00d4aa',
  },
  header: {
    background: 'rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '1.4rem',
    color: '#00d4aa',
    whiteSpace: 'nowrap',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '0.72rem',
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  select: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: '#e8e8f0',
    padding: '8px 12px',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  printBtn: {
    background: '#00d4aa',
    color: '#0f0f1a',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    alignSelf: 'flex-end',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    padding: '24px',
    justifyContent: 'center',
  },
  card: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    width: '240px',
  },
  teamName: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#00d4aa',
    textAlign: 'center',
  },
  qrWrapper: {
    background: '#fff',
    borderRadius: '8px',
    padding: '4px',
  },
  eventInfo: {
    fontSize: '0.72rem',
    color: '#aaa',
    textAlign: 'center',
  },
  lockLabel: {
    fontSize: '0.72rem',
    color: '#00d4aa',
    background: 'rgba(0,212,170,0.15)',
    border: '1px solid rgba(0,212,170,0.4)',
    borderRadius: '20px',
    padding: '3px 10px',
  },
  empty: {
    padding: '48px',
    textAlign: 'center',
    color: '#aaa',
  },
};

export default CameraQR;
